/**
 * PlotlyVisualization - Wrapper component for Plotly 3D visualizations
 *
 * Features:
 * - Auto-rotation on load for demo purposes
 * - Stops rotation on user interaction (click/drag)
 * - Resumes manual control after first interaction
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { scaleEyeForZoom } from './plotlyCamera';

interface PlotlyVisualizationProps {
  src: string;
  className?: string;
  title?: string;
  /** Rotation speed in degrees per frame (default: 0.5) */
  rotationSpeed?: number;
  /** Whether to auto-rotate on load (default: true) */
  autoRotate?: boolean;
}

interface PlotlyInstance {
  relayout: (graphDiv: HTMLElement, update: Record<string, unknown>) => Promise<void>;
  // Re-reads the container size and redraws (including the WebGL context). Needed on
  // orientation/size changes - relayout alone doesn't always resize the gl scene on iOS.
  Plots?: { resize: (graphDiv: HTMLElement) => Promise<void> | void };
}

interface PlotlyLayout {
  scene?: {
    camera?: {
      eye?: { x: number; y: number; z: number };
    };
  };
}

interface PlotlyGraphDiv extends HTMLElement {
  layout?: PlotlyLayout;
  _fullLayout?: PlotlyLayout;
}

// Top margin (px) preserved when filling the box, leaving room for the figure title.
const TITLE_MARGIN_TOP = 48;

// Below this iframe width, stack Plotly's modebar vertically so it clears the title
// instead of overlapping it. At/above it (desktop), the modebar stays horizontal.
// Matches Tailwind's `md` breakpoint - keep in sync if that convention changes.
const MODEBAR_MIN_WIDTH = 768;

/**
 * Wire two-finger pinch-to-zoom on a Plotly 3D graph div by scaling the camera's eye
 * vector (distance from the scene). Listeners live on the graph div inside the iframe and
 * are torn down with the iframe document, so no explicit removal is needed.
 */
function setupPinchZoom(graphDiv: PlotlyGraphDiv, Plotly: PlotlyInstance): void {
  // Bind only once per graph div: these listeners live on the in-iframe element and have
  // no separate removal path, so re-running on a repeated load would stack handlers and
  // compound the zoom on every gesture.
  if (graphDiv.dataset.pinchBound === 'true') {
    return;
  }
  graphDiv.dataset.pinchBound = 'true';

  let prevDistance: number | null = null;

  const fingerDistance = (touches: TouchList) =>
    Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

  graphDiv.addEventListener(
    'touchstart',
    (e: TouchEvent) => {
      if (e.touches.length === 2) {
        prevDistance = fingerDistance(e.touches);
      }
    },
    { passive: false }
  );

  graphDiv.addEventListener(
    'touchmove',
    (e: TouchEvent) => {
      if (e.touches.length !== 2 || prevDistance === null) {
        return;
      }
      e.preventDefault();
      const distance = fingerDistance(e.touches);
      const ratio = distance > 0 ? prevDistance / distance : 1;
      prevDistance = distance;

      const eye = (graphDiv._fullLayout ?? graphDiv.layout)?.scene?.camera?.eye;
      if (!eye) {
        return;
      }
      Plotly.relayout(graphDiv, {
        'scene.camera.eye': scaleEyeForZoom(eye, ratio),
      }).catch((error: unknown) => {
        // A gesture that outlives the viewer (e.g. pinching while the modal closes) rejects
        // benignly - the graph div is detached by then. Only surface failures that happen
        // while the plot is still mounted, so real errors are observable without warn spam.
        if (graphDiv.isConnected) {
          console.warn('PlotlyVisualization: pinch-zoom relayout failed', error);
        }
      });
    },
    { passive: false }
  );

  graphDiv.addEventListener('touchend', (e: TouchEvent) => {
    if (e.touches.length < 2) {
      prevDistance = null;
    }
  });
}

export function PlotlyVisualization({
  src,
  className = '',
  title = 'Visualization',
  rotationSpeed = 0.5,
  autoRotate = true,
}: PlotlyVisualizationProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const angleRef = useRef(0);
  const isRotatingRef = useRef(autoRotate);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const windowResizeHandlerRef = useRef<(() => void) | null>(null);
  const pendingResizeRef = useRef(0);

  // The iframe stays hidden until the plot has been sized, so the export's baked-in 640px
  // render is never shown snapping to fit. Revealed (with a fade) once sizing completes.
  const [ready, setReady] = useState(false);

  // Cancel the auto-rotation animation and the readiness poll only. Deliberately does NOT
  // touch the resize handlers: this runs on first user interaction (see stopRotation), and
  // resizing must keep working after the user grabs the scene.
  const cancelAnimation = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Disconnect the resize observer and window listeners. Only on unmount / src change /
  // re-load - never on user interaction.
  const teardownResizeHandlers = useCallback(() => {
    if (resizeObserverRef.current !== null) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (windowResizeHandlerRef.current !== null) {
      window.removeEventListener('resize', windowResizeHandlerRef.current);
      window.removeEventListener('orientationchange', windowResizeHandlerRef.current);
      windowResizeHandlerRef.current = null;
    }
    // Cancel any resize scheduled for the next frame, so it can't run against a detached
    // graph div (or race a new iframe's setup) after teardown.
    cancelAnimationFrame(pendingResizeRef.current);
    pendingResizeRef.current = 0;
  }, []);

  // Stop rotation when the user interacts. Stops only the animation - resize handling stays
  // alive so the plot still tracks orientation/size changes after a manual rotate or pinch.
  const stopRotation = useCallback(() => {
    isRotatingRef.current = false;
    cancelAnimation();
  }, [cancelAnimation]);

  // Handle iframe load: make the embedded plot fill the iframe (the production exports
  // bake in a fixed 640x640 size with no responsive config), then set up auto-rotation.
  const handleIframeLoad = useCallback(() => {
    if (!iframeRef.current) {
      return;
    }

    const iframe = iframeRef.current;
    // Tear down any setup from a previous load of this same iframe before re-attaching,
    // so a second load event (without a src change) can't orphan the old observer/window
    // listeners or stack duplicate handlers.
    cancelAnimation();
    teardownResizeHandlers();
    setReady(false);

    // Poll until Plotly has initialized inside the iframe, then size + reveal. Polling
    // (rather than a fixed delay) reveals as soon as the plot is ready, keeping the load
    // snappy while still tolerating heavy exports. Give up after ~4s (80 x 50ms) - enough
    // headroom for a slow phone still parsing a large export - and reveal as-is.
    const MAX_SETUP_ATTEMPTS = 80;
    let attempts = 0;
    const trySetup = () => {
      try {
        const iframeWindow = iframe.contentWindow;
        const Plotly = (iframeWindow as unknown as { Plotly?: PlotlyInstance } | null)?.Plotly;
        const iframeDoc = iframe.contentDocument;
        // Plotly typically uses a div with class 'plotly-graph-div' or 'js-plotly-plot'
        const element =
          iframeDoc?.querySelector('.js-plotly-plot') ??
          iframeDoc?.querySelector('.plotly-graph-div') ??
          iframeDoc?.querySelector('[class*="plotly"]');

        if (!iframeWindow || !Plotly || !iframeDoc || !element) {
          // Not initialized yet - retry briefly, then give up and reveal as-is.
          if (attempts < MAX_SETUP_ATTEMPTS) {
            attempts += 1;
            timeoutRef.current = setTimeout(trySetup, 50);
          } else {
            setReady(true);
          }
          return;
        }

        // Cast to PlotlyGraphDiv to access Plotly-specific properties
        const graphDiv = element as PlotlyGraphDiv;

        // Strip the export's default body margin and lock scrolling so the plot can fill
        // the iframe edge-to-edge. touch-action:none lets a finger drag rotate the 3D
        // scene instead of triggering the browser's pan/back-swipe navigation.
        const docEl = iframeDoc.documentElement;
        if (docEl) {
          docEl.style.margin = '0';
        }
        if (iframeDoc.body) {
          iframeDoc.body.style.margin = '0';
          iframeDoc.body.style.overflow = 'hidden';
          iframeDoc.body.style.touchAction = 'none';
        }
        graphDiv.style.touchAction = 'none';

        // Resize the plot to the iframe's current box. Runs on load and whenever the
        // iframe (e.g. the modal) changes size, so it stays responsive on any viewport.
        // Reveal the iframe once the first resize has redrawn, so the size change is never
        // visible.
        const resizePlot = () => {
          const width = iframe.clientWidth;
          const height = iframe.clientHeight;
          if (width <= 0 || height <= 0) {
            return;
          }
          // The export hard-codes the div's pixel size; Plotly.relayout redraws the plot
          // but won't shrink that div, so we set it ourselves, then relayout to redraw
          // into the new box. Collapse the export's default ~80px side/bottom margins so
          // the 3D scene fills the box (otherwise on a phone it renders into a tiny
          // corner), but keep a top margin so the figure title is not overlapped.
          graphDiv.style.width = `${width}px`;
          graphDiv.style.height = `${height}px`;
          Plotly.relayout(graphDiv, {
            width,
            height,
            margin: { l: 0, r: 0, b: 0, t: TITLE_MARGIN_TOP },
            // On narrow viewports stack the modebar vertically down the right edge so it
            // clears the left-aligned title - the toolbar stays fully available, just in a
            // thin column. Horizontal on desktop where there is room.
            'modebar.orientation': width < MODEBAR_MIN_WIDTH ? 'v' : 'h',
          })
            .then(() => {
              // Force the gl scene to redraw at the new size. relayout updates the layout
              // values, but on orientation changes (notably iOS Safari) the WebGL context
              // isn't resized without an explicit Plots.resize, leaving the scene offset.
              void Plotly.Plots?.resize(graphDiv);
              setReady(true);
            })
            .catch(() => setReady(true));
        };

        resizePlot();

        // Coalesce every resize trigger into a single deferred pass (one relayout + gl
        // resize per frame) so a burst of events doesn't fire several WebGL redraws. The
        // rAF defer also lets iOS settle its dimensions, which it can report stale
        // mid-rotation.
        const scheduleResize = () => {
          cancelAnimationFrame(pendingResizeRef.current);
          pendingResizeRef.current = requestAnimationFrame(resizePlot);
        };

        // Track later size changes (modal resize, device rotation).
        const observer = new ResizeObserver(scheduleResize);
        observer.observe(iframe);
        resizeObserverRef.current = observer;

        // Belt-and-suspenders for orientation changes: iOS Safari does not reliably fire
        // the ResizeObserver on rotation, which would otherwise leave the plot at its old
        // size (and the modebar off-screen).
        window.addEventListener('resize', scheduleResize);
        window.addEventListener('orientationchange', scheduleResize);
        windowResizeHandlerRef.current = scheduleResize;

        // Pinch-to-zoom: Plotly's native 3D pinch does not fire here, so drive the camera
        // ourselves. Two fingers spreading apart pulls the camera toward the scene (zoom
        // in); pinching together pushes it back out. We scale the camera's eye vector,
        // which is the same lever the auto-rotation uses.
        // Intentionally before the autoRotate early-return: pinch-zoom must work whether or
        // not auto-rotation is enabled. Do not move the return above this.
        setupPinchZoom(graphDiv, Plotly);

        if (!autoRotate) {
          return;
        }

        // Get initial camera position
        const layout: PlotlyLayout | undefined = graphDiv._fullLayout ?? graphDiv.layout;
        const initialEye = layout?.scene?.camera?.eye ?? { x: 1.25, y: 1.25, z: 1.25 };
        const radius = Math.sqrt(initialEye.x ** 2 + initialEye.y ** 2);

        // Calculate initial angle from current camera position
        angleRef.current = Math.atan2(initialEye.y, initialEye.x) * (180 / Math.PI);

        // Set up event listeners to stop rotation on user interaction
        const stopEvents = ['mousedown', 'touchstart', 'wheel'];
        stopEvents.forEach((event) => {
          graphDiv.addEventListener(event, stopRotation, { once: true });
        });

        // Animation function
        const animate = () => {
          if (!isRotatingRef.current) {
            return;
          }

          angleRef.current += rotationSpeed;
          const radians = (angleRef.current * Math.PI) / 180;

          const newEye = {
            x: radius * Math.cos(radians),
            y: radius * Math.sin(radians),
            z: initialEye.z,
          };

          Plotly.relayout(graphDiv, {
            'scene.camera.eye': newEye,
          }).catch(() => {
            // Ignore errors during rotation (e.g., if component unmounts)
          });

          animationFrameRef.current = requestAnimationFrame(animate);
        };

        // Start animation
        if (isRotatingRef.current) {
          animationFrameRef.current = requestAnimationFrame(animate);
        }
      } catch {
        // Cross-origin or other access issues - we can't manage the plot, so just reveal
        // the iframe as-is rather than leaving it hidden forever.
        setReady(true);
      }
    };

    trySetup();
  }, [autoRotate, rotationSpeed, stopRotation, cancelAnimation, teardownResizeHandlers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isRotatingRef.current = false;
      cancelAnimation();
      teardownResizeHandlers();
    };
  }, [cancelAnimation, teardownResizeHandlers]);

  // Reset rotation state when src changes - cancel old animation first. The reveal state
  // is reset in handleIframeLoad (which fires when the new src loads), so switching
  // visualizations re-hides and fades in cleanly without flashing the new export's size.
  useEffect(() => {
    cancelAnimation();
    teardownResizeHandlers();
    isRotatingRef.current = autoRotate;
  }, [src, autoRotate, cancelAnimation, teardownResizeHandlers]);

  return (
    <iframe
      ref={iframeRef}
      src={src}
      className={className}
      title={title}
      onLoad={handleIframeLoad}
      data-viewer-ready={ready}
      style={{
        // Let a finger drag rotate the 3D scene instead of the browser treating it as a
        // page pan or edge back-swipe.
        touchAction: 'none',
        // Hidden until the plot is sized, then fade in - so the size snap is never seen.
        opacity: ready ? 1 : 0,
        transition: 'opacity 150ms ease-in',
      }}
    />
  );
}
